import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from '@tanstack/react-router';
import { getGetMyCarriersListQueryKey, useInviteCarrier } from '@uturn/api/v1';
import { Permission } from '@uturn/portal/abac';
import { Page } from '@uturn/portal/components/page';
import {
	type InviteCarrierFormValues,
	inviteCarrierSchema,
} from '@uturn/portal/modules/carriers/schema';
import { queryClient } from '@uturn/portal/query';
import {
	Button,
	Card,
	CardDescription,
	CardHeader,
	CardTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	sonner,
	Textarea,
} from '@uturn/ui-kit';
import { AllowedTo } from 'react-abac';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export function CarriersInvite() {
	const router = useRouter();
	const { t } = useTranslation();

	const { mutate, isPending } = useInviteCarrier();

	const form = useForm<InviteCarrierFormValues>({
		resolver: zodResolver(inviteCarrierSchema),
		mode: 'onChange',
	});

	const onValidSendRequest = async (data: InviteCarrierFormValues) => {
		mutate(
			{ data },
			{
				onSuccess: () => {
					queryClient.resetQueries({
						queryKey: getGetMyCarriersListQueryKey(),
					});

					sonner.success(
						t(
							'pages.add-carrier.form.submit.success.description',
							`Successfully sent invited carrier "{{email}}"`,
							{
								email: data.contactEmail,
							},
						),
					);

					router.navigate({ to: '/carriers' });
				},
				onError: () => {
					sonner.error(
						t(
							'pages.add-carrier.form.submit.error.description',
							'Was not able to add new carrier.',
						),
					);
				},
			},
		);
	};

	return (
		<>
			<Helmet title={t('pages.add-carrier.title', 'Add Carrier').toString()} />
			<Page
				backButton={{
					label: t('general.back', 'Back'),
					href: '/carriers',
				}}
				title={t('pages.add-carrier.title', 'Add Carrier').toString()}
				className={'m-auto md:max-w-[550px]'}
			>
				<Card>
					<AllowedTo
						perform={Permission.INVITE_CARRIER}
						no={() => (
							<CardHeader>
								<CardTitle>
									{t(
										'pages.carriers.actions-denied.add.title',
										'Waiting for approval',
									)}
								</CardTitle>
								<CardDescription>
									{t(
										'pages.carriers.actions-denied.add.description',
										'Cannot invite contract carrier while your organisation is pending approval by UTURN.',
									)}
								</CardDescription>
							</CardHeader>
						)}
					>
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(onValidSendRequest)}
								className="space-y-8 p-4"
							>
								<FormField
									control={form.control}
									name="contactOrganisationName"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t(
													'pages.add-carrier.form.organisation.label',
													'Organisation name',
												).toString()}
												*
											</FormLabel>
											<FormControl>
												<Input
													placeholder={t(
														'pages.add-carrier.form.organisation.placeholder',
														'Acme corp',
													).toString()}
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="contactName"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t(
													'pages.add-carrier.form.name.label',
													'Contact name',
												).toString()}
												*
											</FormLabel>
											<FormControl>
												<Input
													placeholder={t(
														'pages.add-carrier.form.name.placeholder',
														'John',
													).toString()}
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="contactEmail"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t(
													'pages.add-carrier.form.email.label',
													'Contact email',
												).toString()}
												*
											</FormLabel>
											<FormControl>
												<Input
													placeholder={t(
														'pages.add-carrier.form.email.placeholder',
														'john_doe@example.com',
													).toString()}
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="inviteMessage"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t(
													'pages.add-carrier.form.message.label',
													'Message',
												).toString()}
											</FormLabel>
											<FormControl>
												<Textarea
													placeholder={t(
														'pages.add-carrier.form.message.placeholder',
														'I would like to invite you to join as a carrier.',
													).toString()}
													className="resize-none"
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<Button
									type="submit"
									disabled={!form.formState.isValid || isPending}
								>
									{t('pages.add-carrier.form.button', 'Add carrier')}
								</Button>
							</form>
						</Form>
					</AllowedTo>
				</Card>
			</Page>
		</>
	);
}
